import React, { useEffect, useState, useRef } from 'react';
import { trackReviewNav } from '../utils/analytics';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" width="18" height="18" role="img" aria-label="Google-vélemény">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const AVATAR_BG = [
  '#4285F4', '#EA4335', '#FBBC05', '#34A853',
  '#9C27B0', '#FF6D00', '#00ACC1', '#F06292',
];

const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(n => n[0]).join('').toUpperCase();

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const days = Math.floor((Date.now() - d) / 86400000);
  if (days === 0) return 'Ma';
  if (days === 1) return '1 napja';
  if (days < 7) return `${days} napja`;
  if (days < 14) return '1 hete';
  if (days < 30) return `${Math.floor(days / 7)} hete`;
  if (days < 60) return '1 hónapja';
  if (days < 365) return `${Math.floor(days / 30)} hónapja`;
  if (days < 730) return '1 éve';
  return `${Math.floor(days / 365)} éve`;
};

const ReviewCard = ({ review, colorIndex }) => {
  const initials = getInitials(review.name);
  const bg = AVATAR_BG[colorIndex % AVATAR_BG.length];
  const timeLabel = review.relativeTime || getRelativeTime(review.date);

  return (
    <div
      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full"
      data-testid={`carousel-review-${review.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ background: bg }}
          >
            {initials}
          </div>
          <div>
            <p className="font-semibold text-ink text-sm leading-tight">{review.name}</p>
            {timeLabel && (
              <p className="text-xs text-gray-500 mt-0.5">{timeLabel}</p>
            )}
          </div>
        </div>
        <GoogleIcon />
      </div>

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={14}
            className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200 fill-gray-200'}
          />
        ))}
      </div>

      {/* Text */}
      <p className="text-gray-600 text-sm leading-relaxed flex-1 line-clamp-5">
        {review.text}
      </p>

      {review.store && (
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-50">{review.store}</p>
      )}
    </div>
  );
};

/**
 * @param perPage        hány kártya látszik egyszerre desktopon (alap: 3).
 * @param mobilePerPage  hány kártya látszik mobilon (alap: 1 – a korábbi
 *                       viselkedés). A főoldal 2-t kér; a /uzletek és a landing
 *                       oldalak alapon maradnak, tehát ott semmi nem változik.
 * @param onNavigate a látogató több véleményt kért (nyíl vagy pötty). A
 *                   komponens NEM tud a mérésről – a hívó dönti el, mit jelent.
 */
const ReviewCarousel = ({ reviews = [], autoPlayInterval = 8000, perPage = 3, mobilePerPage = 1, onNavigate }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // reduced-motion: az automatikus lapozás mozgás – ilyen beállításnál eleve
  // szünetel, és a mouseleave/blur sem indíthatja újra (a nyilakkal/pöttyökkel
  // kézzel továbbra is lapozható).
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ).current;
  const [isPaused, setIsPaused] = useState(reducedMotion);
  const intervalRef = useRef(null);

  const total = reviews.length;
  const maxIndex = Math.max(0, total - perPage);

  useEffect(() => {
    if (isPaused || total <= perPage) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, autoPlayInterval);
    return () => clearInterval(intervalRef.current);
  }, [isPaused, total, maxIndex, autoPlayInterval, perPage]);

  const goNext = () => {
    clearInterval(intervalRef.current);
    trackReviewNav('next', 'velemeny_carousel');
    if (onNavigate) onNavigate('next');
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goPrev = () => {
    clearInterval(intervalRef.current);
    trackReviewNav('prev', 'velemeny_carousel');
    if (onNavigate) onNavigate('prev');
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  if (!total) {
    return (
      <div className="text-center text-gray-500 py-8" data-testid="carousel-empty">
        Még nincs vélemény.
      </div>
    );
  }

  const visible = reviews.slice(currentIndex, currentIndex + perPage);
  while (visible.length < perPage && total >= perPage) {
    visible.push(reviews[visible.length - perPage + currentIndex % total] || reviews[visible.length % total]);
  }

  return (
    <div
      className="relative"
      data-testid="review-carousel"
      role="region"
      aria-roledescription="lapozó"
      aria-label="Vásárlói vélemények"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => { if (!reducedMotion) setIsPaused(false); }}
      /* Az autoplay eddig CSAK egérre állt meg (WCAG 2.2.2): billentyűzetes
         fókusznál a tartalom kicsúszott a fókusz alól, érintőn egyáltalán nem
         volt szüneteltethető. Fókuszra és érintésre is megáll; blur után megy
         tovább. */
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => { if (!reducedMotion && !e.currentTarget.contains(e.relatedTarget)) setIsPaused(false); }}
      onTouchStart={() => setIsPaused(true)}
    >
      {/* Desktop: 3 columns */}
      <div className={`hidden md:grid gap-5 ${perPage === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {reviews.slice(currentIndex, currentIndex + perPage).map((r, i) => (
          <ReviewCard key={`${r.id}-${i}`} review={r} colorIndex={currentIndex + i} />
        ))}
      </div>

      {/* Mobil: annyi kártya, amennyit a perPage kér (egymás alatt). Eddig fixen
          EGY volt, akkor is, ha a hívó kettőt kért – a „2 vélemény” követelmény
          így mobilon nem teljesült volna. */}
      <div className="md:hidden px-1 space-y-4">
        {Array.from({ length: Math.min(mobilePerPage, total) }).map((_, k) => (
          <ReviewCard
            key={`m-${(currentIndex + k) % total}`}
            review={reviews[(currentIndex + k) % total]}
            colorIndex={currentIndex + k}
          />
        ))}
      </div>

      {/* Navigation */}
      {total > perPage && (
        <div className="flex items-center justify-center gap-3 mt-7">
          <button
            onClick={goPrev}
            className="relative w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-brand hover:text-brand transition-colors after:absolute after:-inset-1 after:content-['']"
            data-testid="carousel-prev"
            aria-label="Előző"
          >
            <ChevronLeft size={18} />
          </button>

          {/* A pöttyök koppintási felülete: WCAG 2.5.8 minimuma 24×24 px.
              KORÁBBAN `gap-1.5` (6 px) + `-inset-x-[3px]` → a felület csak
              14 px SZÉLES volt (a magassága 44, az rendben). Külső auditon
              mérve: a szomszédos vélemény jött be koppintásra.
              A vízszintes kiterjesztés 8 px-re nőtt (8+8+8 = 24), és emiatt a
              köz is 16 px-re – enélkül a szomszédos felületek ÁTFEDNÉNEK, és a
              DOM-ban későbbi pötty nyerné az átfedést, ami rosszabb a mostani
              állapotnál. A pötty MÉRETE változatlan (8 px), csak ritkábban áll. */}
          <div className="flex items-center gap-4">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => { trackReviewNav('dot', 'velemeny_carousel'); if (onNavigate) onNavigate('dot'); clearInterval(intervalRef.current); setCurrentIndex(i); }}
                className={`relative rounded-full transition-all duration-300 after:absolute after:-inset-x-[8px] after:-inset-y-[18px] after:content-[''] ${
                  i === currentIndex ? 'w-6 h-2 bg-brand' : 'w-2 h-2 bg-gray-300 hover:bg-brand/50'
                }`}
                data-testid={`carousel-dot-${i}`}
                aria-label={`${i + 1}. vélemény`}
              />
            ))}
          </div>

          <button
            onClick={goNext}
            className="relative w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-brand hover:text-brand transition-colors after:absolute after:-inset-1 after:content-['']"
            data-testid="carousel-next"
            aria-label="Következő"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* MEGJEGYZÉS: itt korábban EGY MÁSODIK pötty-sor állt (`flex md:hidden`,
          egy pötty minden véleményhez). Mobilon így KÉT lapozó látszott
          egyszerre: a fenti nyíl+pötty sor (annak nincs `md:` előtagja, tehát
          mobilon is renderelődik) és ez. Ráadásul ennek a sornak a gombjai NEM
          hívtak trackReviewNav-ot, tehát a mobil lapozás mérés nélkül maradt.
          A duplikátum törölve; a fenti, mért navigáció szolgálja ki mindkét
          töréspontot. */}
    </div>
  );
};

export default ReviewCarousel;
